import { useState, useCallback, useEffect } from 'react';
import { useLightswitch } from './useLightswitch.js';

/**
 * Lightswitch React Entitlement Hook
 * 
 * Hook for checking feature entitlements with automatic state management.
 */


/**
 * Hook to check and manage entitlement status for a feature or general access
 * @param {string} [feature] - Feature identifier to check (optional, defaults to 'has_access')
 * @param {Object} [options={}] - Options for entitlement checking
 * @param {boolean} [options.checkOnMount=true] - Whether to check entitlement when component mounts
 * @returns {Object} Entitlement state and methods
 */
const useEntitlement = function (feature) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const {
    checkOnMount = true
  } = options;
  const {
    checkEntitlement,
    isInitialized,
    isAuthenticated,
    // If no specific feature is requested, use the provider's general entitlement state
    entitled: providerEntitled,
    entitlementLoading: providerLoading,
    entitlementError: providerError,
    paywallConfigId: providerPaywallConfigId,
    lastEntitlementCheck: providerLastChecked
  } = useLightswitch();
  const [entitlementState, setEntitlementState] = useState({
    entitled: false,
    loading: false,
    error: null,
    paywallConfigId: null,
    lastChecked: null
  });

  // Check entitlement function
  const checkFeatureEntitlement = useCallback(async () => {
    if (!isInitialized || !isAuthenticated) {
      const errorResult = {
        entitled: false,
        error: isAuthenticated ? null : 'User not authenticated',
        paywallConfigId: null,
        feature
      };
      setEntitlementState(prev => ({
        ...prev,
        entitled: false,
        loading: false,
        error: isAuthenticated ? null : 'User not authenticated'
      }));
      return errorResult;
    }
    setEntitlementState(prev => ({
      ...prev,
      loading: true,
      error: null
    }));
    try {
      const result = await checkEntitlement(feature);
      setEntitlementState({
        entitled: result.entitled,
        loading: false,
        error: result.error,
        paywallConfigId: result.paywallConfigId,
        lastChecked: new Date()
      });
      return result; // Return the full result object
    } catch (error) {
      console.error(`Error checking entitlement for ${feature}:`, error);
      const errorResult = {
        entitled: false,
        error: error.message || 'Failed to check entitlement',
        paywallConfigId: null,
        feature
      };
      setEntitlementState(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to check entitlement'
      }));
      return errorResult;
    }
  }, [feature, checkEntitlement, isInitialized, isAuthenticated]);

  // Check entitlement on mount and when dependencies change
  // Only check for specific features, not for general access (provider handles that)
  useEffect(() => {
    if (checkOnMount && feature && feature !== 'has_access' && isInitialized) {
      console.log(`🔍 useEntitlement checking specific feature: ${feature}`);
      checkFeatureEntitlement();
    }
  }, [feature, isInitialized, isAuthenticated, checkOnMount, checkFeatureEntitlement]);
  
  // If no specific feature is requested OR if it's the general "has_access" feature, 
  // return the provider's general entitlement state
  if (!feature || feature === 'has_access') {
    console.log('🔄 useEntitlement returning provider state for general access', {
      entitled: providerEntitled,
      loading: providerLoading,
      error: providerError
    });
    return {
      // State from provider (general access check)
      entitled: providerEntitled,
      loading: providerLoading,
      error: providerError,
      paywallConfigId: providerPaywallConfigId,
      lastChecked: providerLastChecked,
      // Methods - use provider's methods for general access
      check: () => Promise.resolve({ entitled: providerEntitled, feature: 'has_access' }),
      refresh: () => Promise.resolve({ entitled: providerEntitled, feature: 'has_access' }),
      // Computed values
      isReady: isInitialized && !providerLoading,
      hasError: !!providerError
    };
  }
  
  // For specific features, use local state
  return {
    // State
    entitled: entitlementState.entitled,
    loading: entitlementState.loading,
    error: entitlementState.error,
    paywallConfigId: entitlementState.paywallConfigId,
    lastChecked: entitlementState.lastChecked,
    // Methods
    check: checkFeatureEntitlement,
    refresh: checkFeatureEntitlement,
    // Same as check since no caching

    // Computed values
    isReady: isInitialized && !entitlementState.loading,
    hasError: !!entitlementState.error
  };
};

export { useEntitlement };
//# sourceMappingURL=useEntitlement.js.map
